/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/bolmateInput.scss';

import React, {
    ChangeEventHandler,
    CSSProperties,
    DetailedHTMLProps,
    FocusEventHandler,
    InputHTMLAttributes,
    KeyboardEventHandler,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';

import {ElementPosition, ErrorType, OrnamentShape} from '@dvrd/dvr-controls';

import classNames from 'classnames';

import {COLOR_BLUE_BOLMATE} from '../../../util/constants';

import {BolmateControlShape, BolmateControlSize} from '../../../util/types';

interface Props {
    onChange: ChangeEventHandler;
    onFocus: FocusEventHandler;
    onBlur: FocusEventHandler;
    onKeyDown: KeyboardEventHandler;
    value: string | number;
    disabled?: boolean;
    autoFocus?: boolean;
    autoSelect?: boolean;
    label?: string;
    ornaments?: OrnamentShape | OrnamentShape[];
    inputProps?: Omit<
        DetailedHTMLProps<
            InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement>,
            HTMLInputElement | HTMLTextAreaElement
        >,
        'crossOrigin'
    >;
    className?: string;
    inputClassName?: string;
    labelClassName?: string;
    ornamentClassName?: string;
    errorClassName?: string;
    error?: ErrorType;
    id: string;
    fullWidth?: boolean;
    active?: boolean;
    area?: boolean;
    placeholder?: string;
    style?: CSSProperties;
    size: BolmateControlSize;
    shape: BolmateControlShape;
    styled?: boolean;
    /** Unified form typography: 0.9rem/500 label, 1rem/500 input text — so
     * mixed controls in one form read as one family. Defaults to false. */
    uniformFont?: boolean;
}

export default function BolmateInput(props: Props) {
    const {
        ornaments,
        autoFocus,
        onFocus,
        autoSelect,
        inputProps,
        inputClassName,
        errorClassName,
        className,
        id,
        area,
        onChange,
        onKeyDown,
        disabled,
        onBlur,
        fullWidth,
        labelClassName,
        ornamentClassName,
        label,
        value,
        error,
        placeholder,
        style,
        size,
        shape,
        styled,
        uniformFont
    } = props;
    const [active, setActive] = useState(autoFocus === true);
    const inputRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
    const hasError = useMemo(() => !!error, [error]);

    function onClickElement() {
        focusInput();
    }

    function onFocusInput(evt: React.FocusEvent) {
        if (!disabled) {
            setActive(true);
            if (autoSelect) {
                const element = inputRef.current;
                if (element) {
                    element.selectionStart = 0;
                    element.selectionEnd = value.toString().length;
                }
            }
        }
        onFocus(evt);
    }

    function onBlurInput(evt: React.FocusEvent) {
        if (!disabled) setActive(false);
        onBlur(evt);
    }

    function focusInput() {
        inputRef.current?.focus();
    }

    function getContentStyle(): CSSProperties {
        const style: CSSProperties = {gridTemplateColumns: '1fr'};
        if (props.active) style.border = `2px solid ${COLOR_BLUE_BOLMATE}`;
        if (!ornaments || (Array.isArray(ornaments) && !ornaments.length)) style.gridTemplateColumns = '1fr';
        let hasLeft: boolean = false,
            hasRight: boolean = false;
        if (Array.isArray(ornaments))
            for (const ornament of ornaments) {
                if (!ornament.placement || ornament.placement === ElementPosition.LEFT) hasLeft = true;
                else if (ornament.placement === ElementPosition.RIGHT) hasRight = true;
                if (hasLeft && hasRight) break;
            }
        else if (ornaments) {
            hasLeft = !ornaments.placement || ornaments.placement === ElementPosition.LEFT;
            hasRight = ornaments.placement === ElementPosition.RIGHT;
        }
        let columns: string = '1fr';
        let paddingLeft: string = '';
        let paddingRight: string = '';
        if (hasRight) {
            columns = `${columns} fit-content(100%)`;
            paddingRight = '.75rem';
        }
        if (hasLeft) {
            columns = `fit-content(100%) ${columns}`;
            paddingLeft = '.75rem';
        }
        style.gridTemplateColumns = columns;
        style.paddingRight = paddingRight;
        style.paddingLeft = paddingLeft;
        return style;
    }

    function renderLabel() {
        if (!label) return null;
        return (
            <label className={classNames('bolmate-input-label', labelClassName)} onClick={onClickElement}>
                {label}
            </label>
        );
    }

    function renderInput() {
        const props = {
            value,
            onChange,
            onFocus: onFocusInput,
            onBlur: onBlurInput,
            onKeyDown,
            disabled,
            className: classNames('bolmate-input', inputClassName),
            id: `${id}-input`,
            placeholder,
            ...inputProps
        };
        if (area) return <textarea {...props} ref={inputRef} />;
        return <input {...props} ref={inputRef} />;
    }

    function renderLeftOrnament() {
        if (!ornaments || (Array.isArray(ornaments) && !ornaments.length)) return null;
        if (Array.isArray(ornaments)) {
            for (const ornament of ornaments) {
                if (!ornament.placement || ornament.placement === ElementPosition.LEFT) return renderOrnament(ornament);
            }
        } else if (!ornaments.placement || ornaments.placement === ElementPosition.LEFT)
            return renderOrnament(ornaments);
        return null;
    }

    function renderRightOrnament() {
        if (!ornaments || (Array.isArray(ornaments) && !ornaments.length)) return null;
        if (Array.isArray(ornaments)) {
            for (const ornament of ornaments) {
                if (ornament.placement === ElementPosition.RIGHT) return renderOrnament(ornament);
            }
        } else if (ornaments.placement === ElementPosition.RIGHT) return renderOrnament(ornaments);
        return null;
    }

    function renderOrnament(ornament: OrnamentShape) {
        if (typeof ornament.element === 'string')
            return (
                <span className={classNames('bolmate-input-ornament', ornamentClassName)} onClick={onClickElement}>
                    {ornament.element}
                </span>
            );
        else
            return React.cloneElement(ornament.element, {
                className: classNames(ornament.element.props.className, ornamentClassName, 'bolmate-input-ornament'),
                onClick: (evt: React.MouseEvent) => {
                    onClickElement();
                    if (React.isValidElement(ornament.element)) {
                        const element: React.ReactElement<any> = ornament.element;
                        element.props.onClick?.(evt);
                    }
                }
            });
    }

    function renderError() {
        return <span className={classNames('bolmate-input-error', errorClassName)}>{error}</span>;
    }

    useEffect(() => {
        if (!disabled && autoFocus) focusInput();
    }, [autoFocus, disabled]);

    return (
        <div
            onClick={onClickElement}
            id={id}
            className={classNames(
                'bolmate-input-container',
                disabled && 'disabled',
                hasError && 'error',
                active && 'active',
                fullWidth && 'full',
                `size-${size}`,
                `shape-${shape}`,
                styled && 'styled',
                uniformFont && 'uniform-font',
                className
            )}
            style={style}
        >
            {renderLabel()}
            <div className='content-container' style={getContentStyle()}>
                {renderLeftOrnament()}
                {renderInput()}
                {renderRightOrnament()}
            </div>
            {renderError()}
        </div>
    );
}
